import axios from '@/plugins/axios';
import { DEFAULT_INTERFACE_COLOR } from '@/utils/interface-theme';
import utils from '@/stores/utils';
import {
    ACTIVE_USER_CHANGED_MESSAGES,
    getActiveUserFingerprint,
    LOGIN_INFO_POLL_INTERVAL_MS,
} from '@/utils/active-user-fingerprint';

/** Concurrent stats fetches share one promise (avoids cancel/re-fetch storms). */
let inflightFetchStats = null;

const IMPERSONATION_COOKIES = ['_impersonate', '_impersonate_provider'];

function readCookie(name) {
    const entry = document.cookie
        .split('; ')
        .find(row => row.startsWith(`${name}=`));
    return entry ? entry.split('=')[1] : null;
}

function clearImpersonationCookies() {
    IMPERSONATION_COOKIES.forEach(name => {
        document.cookie = `${name}=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT`;
    });
}

const state = {
    accepted_terms: false,
    active_locale: 'fr',
    alerts: [],
    connected: null,
    dashboard_header_color_is_dark: true,
    dashboard_header_color: DEFAULT_INTERFACE_COLOR,
    dashboard_logo: null,
    dashboard_title: 'Safetee',
    drawer: true,
    assistant_open: false,
    menu_collapsed: window.localStorage.getItem('menu_collapsed') === '1',
    menu_force_expanded: false,
    entity: null,
    features: [],
    identity_confirmed: null,
    impersonated: null,
    acting_as_other_user: false,
    loading_customer_error: null,
    loading_customer: false,
    loading_logininfo_error: null,
    loading_logininfo: false,
    must_confirm_identity: false,
    operator_domain: false,
    permissions: [],
    scope_codes: [],
    persona: null,
    readonly: false,
    settings: {},
    stats_loaded: false,
    stats_loading_error: null,
    stats_loading: false,
    stats_cancel_source: null,
    stats: {},
    cartItems: 0, // Total count of items in cart (for immediate display)
    login_info_cancel_source: null,
    login_info_poll_timer_id: null,
    active_user_change_pending: false,
    settings_cancel_source: null,
    status: null,
    superuser: false,
    user_mode: null,
    user: null,
    onboarding_completed: false,
    onboarding_answers: {},
    menu_modules_overrides: {},
    catalog_pdf_import_enabled: false,
    navigation_preferences: { hidden_items: {} },
};

import { setCurrentEntityId } from "@/utils/org-storage";


const getters = {
    readonly: state => state.readonly,
    stats: state => state.stats,
    stats_loaded: state => state.stats_loaded,
    trial_ended: state => state.status == 'trial-ended',
    accepted_terms: state => state.accepted_terms,
    onboarding_completed: state => state.onboarding_completed,
    onboarding_answers: state => state.onboarding_answers || {},
    menu_modules_overrides: state => state.menu_modules_overrides || {},
    current_user: state => state.user,
    current_user_id: state => state.user?.id,
    current_user_language: state => state.user?.language,
    current_user_connected: state => state.connected,
    current_user_impersonated: state => Boolean(
        state.impersonated || state.acting_as_other_user
    ),
    current_user_identity_confirmed: state => state.identity_confirmed,
    current_user_must_confirm_identity: state => state.must_confirm_identity,
    current_user_mode: state => state.entity?.profile,
    current_user_superuser: state => state.superuser,
    current_user_permissions: state => state.permissions,
    current_user_scopes: state => state.scope_codes,
    current_user_is_owner: state => state.user?.is_owner || false,
    current_entity: state => state.entity,
    current_entity_name: state => state.entity?.name,
    current_entity_settings: state => state.settings,
    current_entity_persona: state => state.persona,
    current_entity_stats: state => state.stats,
    current_entity_features: state => state.features,
    current_entity_profile: state => state.entity?.profile,
    current_entity_id: state => state.entity?.id,
    navigation_preferences: state => state.navigation_preferences,

    trial_remaining_days(state) {
        if (state.status != 'trial') {
            return null;
        }

        if (!state.trial_end) {
            return null;
        }
        const now = new Date();
        const end = new Date(state.trial_end);
        const diff = end.getTime() - now.getTime();
        return Math.ceil(diff / (1000 * 3600 * 24));
    },
};

const mutations = {

    updateActiveLocale(state, locale) {
        state.active_locale = locale;
    },

    updateCartItems(state, count) {
        state.cartItems = count;
    },

    updateStats(state, stats) {
        state.stats = stats || {};
        state.stats_loaded = true;
    },

    updateUserMode(state, mode) {
        state.user_mode = mode;
    },

    updateStatsLoading(state, loading) {
        state.stats_loading = loading;
    },

    updateStatsLoadingError(state, error) {
        state.stats_loading_error = error;
    },
    updateStatsCancelSource(state, cancel_source) {
        state.stats_cancel_source = cancel_source;
    },
    updateLoginInfoCancelSource(state, cancel_source) {
        state.login_info_cancel_source = cancel_source;
    },
    updateLoginInfoPollTimerId(state, timerId) {
        state.login_info_poll_timer_id = timerId;
    },
    updateActiveUserChangePending(state, pending) {
        state.active_user_change_pending = pending;
    },
    updateSettingsCancelSource(state, cancel_source) {
        state.settings_cancel_source = cancel_source;
    },

    updateDrawer(state, drawer) {
        state.drawer = drawer;
    },

    updateAssistantOpen(state, open) { state.assistant_open = open; },

    updateMenuCollapsed(state, collapsed) {
        state.menu_collapsed = collapsed;
        window.localStorage.setItem('menu_collapsed', collapsed ? '1' : '0');
    },

    updateMenuForceExpanded(state, forced) {
        state.menu_force_expanded = forced;
    },

    updateLoginInfo(state, info) {
        state.connected = info.connected;
        state.accepted_terms = info.accepted_terms;
        state.impersonated = info.impersonated;
        state.acting_as_other_user = info.acting_as_other_user || false;
        state.user = info.user || {};
        state.entity = info.entity;
        state.identity_confirmed = info.identity_confirmed || false;
        state.must_confirm_identity = info.must_confirm_identity || false;
        state.dashboard_title = info.dashboard_title;
        state.operator_domain = info.operator_domain;
        state.dashboard_logo = info.dashboard_logo;
        state.dashboard_logo = info.dashboard_logo;
        state.dashboard_header_color = info.dashboard_header_color || DEFAULT_INTERFACE_COLOR;
        state.dashboard_header_color_is_dark = utils.isColorDark(state.dashboard_header_color);
        state.permissions = info.permissions;
        state.scope_codes = info.scope_codes || [];
        state.features = info.features || [];
        state.readonly = info.readonly;
        state.status = info.status;
        state.trial_end = info.trial_end;
        state.persona = info.entity?.persona;
        state.superuser = info.superuser;
        state.onboarding_completed = info.onboarding_completed || false;
        state.onboarding_answers = info.connected
            ? (info.onboarding_answers || {})
            : {};
        state.menu_modules_overrides = info.connected
            ? (info.menu_modules_overrides || {})
            : {};
        state.catalog_pdf_import_enabled = info.catalog_pdf_import_enabled === true;
        state.navigation_preferences = info.navigation_preferences || { hidden_items: {} };
    },

    updateLoadingLoginInfo(state, loading) {
        state.loading_logininfo = loading;
    },

    updateLoadingLoginInfoError(state, error) {
        state.loading_logininfo_error = error;
    },

    updateLoadingCustomer(state, loading) {
        state.loading_customer = loading;
    },

    updateLoadingCustomerError(state, error) {
        state.loading_customer_error = error;
    },

    appendAlert(state, alert) {
        state.alerts.push(alert);
    },

    removeAlert(state, id) {
        state.alerts = state.alerts.filter((item) => {
            return item.id != id;
        });
    },

    updateSettings(state, settings) {
        state.settings = settings;
    },

    updateNavigationPreferences(state, preferences) {
        state.navigation_preferences = preferences || { hidden_items: {} };
    },

    updateOnboardingAnswers(state, answers) {
        state.onboarding_answers = answers || {};
    },

    updateMenuModuleOverrides(state, overrides) {
        state.menu_modules_overrides = overrides || {};
    },
};

const actions = {

    async activateAgent({ commit, dispatch, state }, params) {

        try {
            const response = await axios.post(`/api/entities/${state.entity.id}/activateagent/`);
            dispatch('fetchLoginInfo');
            return response;
        } catch (xhr_error) {
            throw utils.handleError(xhr_error);
        }
    },

    async fetchSettings({ commit, dispatch, state }, params) {
        if (state.settings_cancel_source) {
            state.settings_cancel_source.cancel("canceled");
        }

        const cancelSource = axios.CancelToken.source();
        commit('updateSettingsCancelSource', cancelSource);

        try {
            const response = await axios.get('/api/settings/', {
                cancelToken: state.settings_cancel_source?.token
            });
            commit('updateSettings', response.data);
            commit('updateSettingsCancelSource', null);
            return response;
        } catch (xhr_error) {
            if (axios.isCancel(xhr_error)) {
                return;
            }
            throw utils.handleError(xhr_error);
        }
    },

    async signIn({ commit, dispatch, state }, params) {
        try {
            const response = await axios.post('/api/account/login/', { username: params.username, password: params.password });
            dispatch('fetchLoginInfo');
            return response;
        } catch (xhr_error) {
            throw utils.handleError(xhr_error);
        }
    },

    async addAlert({ commit, dispatch, state }, params) {
        let alert = {};
        let timeout = 2000;
        if (typeof params == 'string') {
            alert.text = params;
        } else {
            alert.text = params.text;
            alert.textBold = params.textBold;
            alert.textPlain = params.textPlain;
            alert.type = params.type;
            alert.button_route = params.button_route;
            alert.button_text = params.button_text;
            alert.hide_modal = params.hide_modal;
            timeout = params.timeout || timeout;
        }

        alert.id = Math.floor(Math.random() * 1000000);
        commit('appendAlert', alert);
        window.setTimeout(() => {
            commit('removeAlert', alert.id);
        }, timeout);
    },


    async signOut({ commit, dispatch, state }, params) {
        try {
            if (IMPERSONATION_COOKIES.some(name => readCookie(name))) {
                try {
                    await axios.delete('/api/account/impersonation/');
                } catch (xhr_error) {
                    // Signing out must go through even if the impersonation cannot be stopped.
                }
            }
            const response = await axios.delete('/api/account/login/');
            clearImpersonationCookies();
            dispatch('fetchLoginInfo');
            return response;
        } catch (xhr_error) {
            throw utils.handleError(xhr_error);
        }
    },

    async acceptTerms({ commit, dispatch, state }, params) {
        try {
            let response = await axios.post('/api/account/acceptterms/', { accept_terms: true });
            await dispatch('fetchLoginInfo');
        }
        catch (xhr_error) {
            throw utils.handleError(xhr_error);
        }
    },

    async stopImpersonating({ commit, dispatch, state }, params) {
        let nexturl = '/';
        const customer_id = readCookie('_impersonate');
        const provider_id = readCookie('_impersonate_provider');
        if (customer_id) {
            nexturl = `/customers/${customer_id}/`;
        } else if (provider_id) {
            nexturl = `/providers/${provider_id}/`;
        }
        const response = await axios.delete("/api/account/impersonation/");
        window.location = nexturl;
        return response.data;
    },
    
    
    async fetchLoginInfo({ commit, dispatch, state }, params = {}) {
        const silent = params?.silent === true;
        if (!silent) {
            commit('updateLoadingLoginInfo', true);
            commit('updateLoadingLoginInfoError', null);
        }

        if (state.login_info_cancel_source) {
            state.login_info_cancel_source.cancel("canceled");
        }

        const cancelSource = axios.CancelToken.source();
        commit('updateLoginInfoCancelSource', cancelSource);

        try {
            const response = await axios.get('/api/account/login/', {
                cancelToken: state.login_info_cancel_source?.token
            });
            commit('updateLoginInfo', response.data);
            commit('updateLoginInfoCancelSource', null);
            if (response.data.connected && !silent) {
                dispatch('fetchStats');
                dispatch('fetchSettings');
            }
            if (!silent) {
                dispatch('help/fetchUniqueIds', null, { root: true });
            }
            setCurrentEntityId(response.data.user?.entity);
            if (response.data.user?.entity) {
                dispatch('customerorders/hydrateOrgStorageSettings', null, { root: true });
                dispatch('stockstats/hydrateOrgStorageSettings', null, { root: true });
            }
            return response;
        } catch (xhr_error) {
            if (axios.isCancel(xhr_error)) {
                return;
            }
            const error = utils.handleError(xhr_error);
            if (!silent) {
                commit('updateLoadingLoginInfoError', error);
            }
            throw error;
        } finally {
            if (!silent) {
                commit('updateLoadingLoginInfo', false);
            }
        }
    },

    startLoginInfoPolling({ commit, dispatch, state }) {
        if (state.login_info_poll_timer_id) {
            return;
        }

        const timerId = window.setInterval(() => {
            dispatch('pollLoginInfo');
        }, LOGIN_INFO_POLL_INTERVAL_MS);
        commit('updateLoginInfoPollTimerId', timerId);
    },

    stopLoginInfoPolling({ commit, state }) {
        if (!state.login_info_poll_timer_id) {
            return;
        }
        window.clearInterval(state.login_info_poll_timer_id);
        commit('updateLoginInfoPollTimerId', null);
    },

    async pollLoginInfo({ commit, dispatch, state }) {
        if (!state.connected || state.active_user_change_pending) {
            return;
        }

        const previousFingerprint = getActiveUserFingerprint(state);

        try {
            const response = await axios.get('/api/account/login/');
            const nextFingerprint = getActiveUserFingerprint(response.data);

            if (
                previousFingerprint
                && nextFingerprint !== previousFingerprint
            ) {
                await dispatch('handleActiveUserChanged');
                return;
            }

            commit('updateLoginInfo', response.data);
            setCurrentEntityId(response.data.user?.entity);
            if (response.data.user?.entity) {
                dispatch('customerorders/hydrateOrgStorageSettings', null, { root: true });
                dispatch('stockstats/hydrateOrgStorageSettings', null, { root: true });
            }
        } catch {
            // Ignore transient polling errors; next tick will retry.
        }
    },

    async handleActiveUserChanged({ commit, dispatch, state }) {
        if (state.active_user_change_pending) {
            return;
        }
        commit('updateActiveUserChangePending', true);
        dispatch('stopLoginInfoPolling');
        dispatch('inappNotifications/stopPolling', null, { root: true });

        const locale = state.active_locale === 'en' ? 'en' : 'fr';
        const messages = ACTIVE_USER_CHANGED_MESSAGES[locale];

        await new Promise((resolve) => {
            dispatch('swal/show', {
                options: {
                    title: messages.title,
                    text: messages.text,
                    type: 'info',
                    position: 'center',
                    confirmButtonText: messages.confirmButtonText,
                },
                resolve,
            }, { root: true });
        });

        window.location.reload();
    },

    async saveNavigationPreferences({ commit }, preferences) {
        try {
            const response = await axios.put(
                "/api/account/navigation/",
                preferences,
            );
            commit("updateNavigationPreferences", response.data);
            return response.data;
        } catch (xhr_error) {
            throw utils.handleError(xhr_error);
        }
    },

    async impersonateCustomerEntity({ commit, dispatch, state }, params) {
        try {
            const response = await axios.post(`/api/customers/${params.customer}/impersonate/`);
            return response;
        } catch (xhr_error) {
            throw utils.handleError(xhr_error);
        }
    },

    async impersonateProviderEntity({ commit, dispatch, state }, params) {
        try {
            const response = await axios.post(`/api/providers/${params.provider}/impersonate/`);
            return response;
        } catch (xhr_error) {
            throw utils.handleError(xhr_error);
        }
    },


    async fetchStats({ commit, dispatch, state, rootState, rootGetters }, params = {}) {
        // Don't fetch if there are pending cart operations scheduled
        // This prevents overwriting local changes that haven't been synced yet
        // Unless force is true (e.g., after processing pending operations)
        const force = params?.force || false;
        const hasPendingCartOperations = rootGetters['cart/hasPendingOperations'];
        
        if (!force && hasPendingCartOperations) {
            return;
        }

        if (inflightFetchStats && !force) {
            return inflightFetchStats;
        }

        inflightFetchStats = (async () => {
            const url = `/api/stats/`;
            commit('updateStatsLoading', true);
            commit('updateStatsLoadingError', null);

            if (state.stats_cancel_source) {
                state.stats_cancel_source.cancel("canceled");
            }

            const cancelSource = axios.CancelToken.source();
            commit('updateStatsCancelSource', cancelSource);

            try {
                const response = await axios.get(url, {
                    cancelToken: cancelSource.token
                });
                commit('updateStats', response.data);
                if (
                    !rootGetters['cart/hasPendingOperations']
                    && rootGetters['cart/totalItemsCount'] === 0
                ) {
                    commit('updateCartItems', response.data?.cartitems);
                }
                commit('updateStatsCancelSource', null);
                return response;
            } catch (xhr_error) {
                if (axios.isCancel(xhr_error)) {
                    return;
                }
                commit('updateStatsLoadingError', utils.handleError(xhr_error));
                throw xhr_error;
            } finally {
                commit('updateStatsLoading', false);
                inflightFetchStats = null;
            }
        })();

        return inflightFetchStats;
    },

    async activateOrganization({ commit, dispatch, state }, params) {
        try {
            const response = await axios.post(`/api/organizations/${params.user_id}/activate/`);
            return response;
        } catch (xhr_error) {
            throw utils.handleError(xhr_error);
        }
    },

    async savePersona({ commit, dispatch, state }, persona) {
        try {
            let response = await axios.patch(`/api/entities/${state.entity.id}/`, { persona: persona });
            await dispatch('fetchLoginInfo');
        }
        catch (xhr_error) {
            throw utils.handleError(xhr_error);
        }
    },

    /**
     * Persist discovery questionnaire answers for the current organization.
     * @param {object} context
     * @param {Record<string, string>} onboarding_answers
     */
    async saveOnboardingAnswers({ commit }, onboarding_answers) {
        try {
            const response = await axios.put('/api/onboarding-answers/', {
                onboarding_answers: onboarding_answers || {},
            });
            commit(
                'updateOnboardingAnswers',
                response.data.onboarding_answers || {},
            );
            return response.data;
        } catch (xhr_error) {
            throw utils.handleError(xhr_error);
        }
    },

    /**
     * Persist menu module overrides (kept for existing API payloads).
     * The sidebar is no longer filtered by the questionnaire.
     * @param {object} context
     * @param {Record<string, boolean>} menu_modules_overrides
     */
    async saveMenuModuleOverrides({ commit }, menu_modules_overrides) {
        try {
            const response = await axios.put('/api/onboarding-answers/', {
                menu_modules_overrides: menu_modules_overrides || {},
            });
            commit(
                'updateMenuModuleOverrides',
                response.data.menu_modules_overrides || {},
            );
            return response.data;
        } catch (xhr_error) {
            throw utils.handleError(xhr_error);
        }
    },
};

export default {
    namespaced: true,
    state,
    getters,
    actions,
    mutations,
};
